<template>
  <v-container fluid
    class="container">
    <Breadcrumbs
      :items="breadcrumbs"/>
    <v-data-table fixed-header
      dense
      class="elevation-1"
      :page.sync="pagination.page"
      :server-items-length="pagination.total || items.length"
      :headers="headers"
      :items="items"
      :footer-props="{
        'items-per-page-options': pagination.itemsPerPage
      }"
      :items-per-page="pagination.size">
      <template v-slot:top>
        <v-toolbar flat>
          <v-col cols="12" sm="3"
            class="px-0">
            <v-text-field
              clearable
              v-model="search"
              append-icon="mdi-magnify"
              :label="translate('plannings_capex_search_items')"
              single-line
              hide-details
            ></v-text-field>
           </v-col>
          <v-spacer></v-spacer>
          <v-btn v-if="editMode" color="primary"
              @click.stop="addNewPlanningItem()">
            <v-icon class="mr-2">mdi-table-large-plus</v-icon>
            {{translate('plannings_new_risk_item')}}
          </v-btn>
        </v-toolbar>
      </template>
      <template v-slot:header="props">
        <thead>
          <tr>
            <th :colspan="headers.length - riskPeriods.length "
              class="text-center">
              {{translate('plannings_risk')}}
            </th>
            <th :colspan="riskPeriods.length"
              class="text-center depreciation">
              {{translate('plannings_risk')}}
            </th>
          </tr>
          <tr>
            <th v-for="(header, idx) in props.headers"
              :key="idx">
              {{header.text}}
            </th>
          </tr>
        </thead>
      </template>
      <template v-slot:body="props">
        <tbody>
          <RiskRow v-for="(item, idx) in props.items"
            v-show="!search || (item.asset_name.indexOf(search) !== -1)"
            :ref="`row-${idx}`"
            :key="item.id || item.tempId"
            :item="item"
            :rowIndex="idx"
            :riskPeriods="riskPeriods"
            @delete="deleteItem" />
          <tr v-if="!props.items.length"
            class="v-data-table__empty-wrapper">
            <td :colspan="headers.length">{{translate('plannings_capex_no_items')}}</td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
    <v-btn color="accent" v-if="editMode"
      fab
      fixed
      right
      bottom
      @click.stop="savePlanning()">
      <v-icon dark>mdi-content-save</v-icon>
    </v-btn>
    <Snackbar :text="notificationText"
      :type="notificationType" />
    <v-overlay :value="loader"
      :z-index="1000">
      <v-progress-circular indeterminate
        size="64"></v-progress-circular>
    </v-overlay>
    <ConfirmDialog
      :dialog="deleteDialog"
      :description="deleteDialogDescription"
      @change="setDialogState"
      @confirmDelete="confirmDeleteAction"
    />
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import RiskRow from '@/components/RiskRow';
import ConfirmDialog from '@/components/ConfirmDialog';
import Snackbar from '../components/Snackbar';
import Breadcrumbs from '../components/Breadcrumbs';
import { generatePlacementPeriodsCapex } from '../utilities/placementPeriod';
import { generateRiskPeriodsValues } from '../utilities/riskPeriodsValues';
import { generateRiskPeriods } from '../utilities/riskPeriods';

import api from '../api/lbt-api';
import store from '../store/index';

const {
  getRiskForPlanning,
  createRiskForPlanning,
  getPlanning,
  deleteRiskForPlanning,
  updateRiskForPlanning,
  salesRecalculate,
  loansRecalculate,
} = api;

export default {
  name: 'RiskPlanning',
  components: {
    Snackbar,
    RiskRow,
    Breadcrumbs,
    ConfirmDialog,
  },
  data() {
    return {
      search: '',
      notificationText: '',
      notificationType: '',
      deleteDialog: false,
      editMode: true,
      deleteDialogDescription: '',
      itemForDeletion: {},
      loader: false,
      headers: [],
      items: [],
      planningInfo: {},
      pagination: {
        page: 1,
        size: 25,
        total: 0,
        itemsPerPage: [25, 50, 100],
      },
      breadcrumbs: [
        {
          translationKey: 'breadcrumbs_plannings',
          route: '/plannings',
          disabled: false,
        },
        {
          translationKey: 'plannings_risk',
          disabled: true,
        },
      ],
      placementPeriodValues: [],
      riskPeriods: [],
    };
  },
  async created() {
    this.headers = [
      {
        text: this.translate('plannings_sales_leasing_type_id'),
        align: 'left',
        sortable: false,
        value: 'leasing_type_id',
      },
      {
        text: this.translate('plannings_sales_segment_id'),
        align: 'left',
        sortable: false,
        value: 'segment_id',
      },
      {
        text: this.translate('plannings_risk_type'),
        align: 'left',
        sortable: false,
        value: 'risk_type_id',
      },
      {
        text: this.translate('plannings_risk_stage'),
        align: 'left',
        sortable: false,
        value: 'stage_id',
      },
      {
        text: this.translate('table_actions'),
        align: 'left',
        sortable: false,
        value: 'actions',
      },
    ];
    try {
      await this.fetchPlanningRiskItems();
      const planning = await getPlanning(this.$route.params.planningId);
      this.planningInfo = planning;
      this.placementPeriodValues = generatePlacementPeriodsCapex(planning.year, planning.month - 1);
      this.riskPeriods = generateRiskPeriods(planning.year, planning.month - 1);
      if (this.planningInfo.locked === 1) {
        this.editMode = false;
      }
    } catch (message) {
      this.notificationText = message;
      this.notificationType = 'error';
      setTimeout(() => { this.notificationText = null; }, 100);
    } finally {
      this.loader = false;
    }
    this.riskPeriods.forEach((el) => {
      this.headers.push({
        text: el,
        align: 'left',
        sortable: false,
        class: 'depreciation',
        value: el,
      });
    });
  },
  computed: {
    ...mapGetters('translations', [
      'translate',
    ]),
  },
  methods: {
    async fetchPlanningRiskItems() {
      const risk = await getRiskForPlanning(this.$route.params.planningId);
      this.items = risk.risk_entries;
    },
    addNewPlanningItem() {
      this.items.unshift({
        tempId: Math.random(),
        leasing_type_id: 0,
        segment_id: 0,
        customer_id: 9,
        product_group_id: 0,
        risk_period: generateRiskPeriodsValues(this.planningInfo.year, this.planningInfo.month - 1),
      });
    },
    async savePlanning() {
      const rowNum = Object.keys(this.$refs).length;
      const riskCreateRequests = [];
      const riskUpdateRequests = [];
      let payloadHasInvalidRow = false;
      if (!rowNum) {
        return;
      }

      for (let iterator = 0; iterator < rowNum; iterator += 1) {
        if (this.$refs[`row-${iterator}`].length) {
          this.$refs[`row-${iterator}`][0].validate();
          if (this.$refs[`row-${iterator}`][0].invalidData) {
            payloadHasInvalidRow = true;
          }
        }
      }

      if (payloadHasInvalidRow) {
        return;
      }

      // TODO: Send all capex items in bulk request
      this.loader = true;
      try {
        for (let iterator = 0; iterator < rowNum; iterator += 1) {
          if (!this.items[iterator].id) {
            riskCreateRequests.push(createRiskForPlanning(this.$route.params.planningId, this.items[iterator]));
          } else {
            riskUpdateRequests.push(updateRiskForPlanning(this.$route.params.planningId, this.items[iterator], this.items[iterator].id));
          }
        }
        await Promise.all(riskCreateRequests);
        await Promise.all(riskUpdateRequests);
        salesRecalculate(this.$route.params.planningId);
        loansRecalculate(this.$route.params.planningId);
        this.notificationText = this.translate('notifications_planning_updated');
        this.notificationType = 'success';
        setTimeout(() => { this.notificationText = null; }, 100);
        await this.fetchPlanningRiskItems();
      } catch (message) {
        this.notificationText = message;
        this.notificationType = 'error';
        setTimeout(() => { this.notificationText = null; }, 100);
      } finally {
        this.loader = false;
      }
    },
    deleteItem(item) {
      this.itemForDeletion = item;
      this.deleteDialog = true;
      this.deleteDialogDescription = this.translate('dialog_delete_action_description');
    },
    async confirmDeleteAction() {
      if (this.itemForDeletion.item.tempId) {
        this.$delete(this.items, this.itemForDeletion.rowIndex);
        this.deleteDialog = false;
        this.itemForDeletion = null;
        return;
      }
      this.loader = true;

      try {
        await deleteRiskForPlanning(this.$route.params.planningId, this.itemForDeletion.item.id);
        this.deleteDialog = false;
        this.itemForDeletion = null;
        this.notificationText = this.translate('notifications_item_deleted_successfully');
        this.notificationType = 'success';
        setTimeout(() => { this.notificationText = null; }, 100);
        await this.fetchPlanningRiskItems();
      } catch (message) {
        this.notificationText = message;
        this.notificationType = 'error';
        setTimeout(() => { this.notificationText = null; }, 100);
        setTimeout(() => { this.notificationText = null; }, 100);
      } finally {
        this.loader = false;
      }
    },
    setDialogState(value) {
      this.deleteDialog = value;
    },
  },
  async beforeRouteEnter(from, to, next) {
    await store.dispatch('finrepBsPl/getOptions');
    next();
  },
};
</script>

<style lang="scss" scoped>

.container {
  padding-bottom: 5rem !important;
}

</style>
